import { motion } from 'framer-motion';
import { Ornament } from '../Card';
import { PillarIcon } from '../../lib/icons';
import { HeroVideo } from '../HeroVideo';
import type { HomeProps } from './types';

const stagger = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } };
const fadeUp = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };

export function BoldHome(p: HomeProps) {
  const videoUrl = p.heroVideoEnabled && p.heroVideoUrl ? p.heroVideoUrl : undefined;
  return (
    <>
      {/* Full-bleed hero */}
      <div className="relative -mx-5 overflow-hidden">
        {videoUrl ? null : <div className="absolute inset-0 bg-gradient-to-b from-oud-800 via-oud-900 to-oud-900" />}
        {videoUrl ? <HeroVideo url={videoUrl} /> : <div className="absolute inset-0 najdi-pattern opacity-10 pointer-events-none" aria-hidden />}

        <motion.div
          variants={stagger}
          initial="hidden"
          animate="visible"
          className="relative px-5 pt-14 pb-14 text-center max-w-md mx-auto space-y-6"
        >
          <motion.span variants={fadeUp}
            className="inline-flex items-center gap-2 rounded-full bg-gold/15 border border-gold-soft/20 text-gold-soft px-4 py-1.5 text-[11px] font-bold tracking-wider">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-gold opacity-75 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-gold" />
            </span>
            {p.instantBadge}
          </motion.span>

          <motion.h1 variants={fadeUp}
            className="text-[2.75rem] md:text-6xl font-black text-sand leading-[1.05] whitespace-pre-line tracking-tight">
            {p.headline}
          </motion.h1>

          <motion.p variants={fadeUp} className="text-cream leading-relaxed text-[15px] max-w-sm mx-auto">
            {p.subheadline}
          </motion.p>

          <motion.div variants={fadeUp} className="pt-3 space-y-3 max-w-xs mx-auto">
            <button type="button" onClick={p.onCamera}
              className="w-full h-14 rounded-2xl font-bold text-lg bg-gold text-oud-900 shadow-soft hover:bg-gold-soft active:scale-[0.98] transition-all">
              {p.ctaLabel}
            </button>
            <button type="button" onClick={p.onGallery}
              className="w-full text-sm font-bold py-2 transition-colors"
              style={{ color: 'rgba(255,255,255,0.75)' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.75)'; }}>
              {p.ctaUploadLabel}
            </button>
          </motion.div>
        </motion.div>

        {/* Curved bottom edge — overlaps into next section */}
        <div className="absolute bottom-[-1px] inset-x-0">
          <svg viewBox="0 0 1440 48" className="w-full h-8 text-sand block" preserveAspectRatio="none">
            <path fill="currentColor" d="M0 48h1440V20C1200 40 960 48 720 48S240 40 0 20v28Z" />
          </svg>
        </div>
      </div>

      {/* Pillars — seamless infinite marquee with faded edges */}
      <div className="py-5 overflow-hidden" dir="ltr"
        style={{ maskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)', WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)' }}>
        <div className="flex w-max animate-marquee gap-4">
          {[...p.pillars, ...p.pillars].map((pl, i) => (
            <div
              key={`${pl.label}-${i}`}
              className="flex-none bg-cream rounded-2xl border border-oud-100/60 px-4 py-3.5 flex items-center gap-3 shadow-soft"
              dir="rtl"
            >
              <div className="flex-none w-10 h-10 rounded-xl bg-gradient-to-br from-oud-800 to-oud-900 text-gold flex items-center justify-center shadow-soft">
                <PillarIcon name={pl.icon} />
              </div>
              <span className="text-sm font-bold text-oud-900 leading-tight whitespace-nowrap">{pl.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Steps */}
      <section className="mt-4">
        <div className="flex items-center gap-3 mb-5">
          <Ornament className="flex-1" />
          <h2 className="text-sm font-bold text-oud-700 tracking-widest">{p.stepsTitle}</h2>
          <Ornament className="flex-1" />
        </div>
        <div className="space-y-3">
          {p.steps.map((step, i) => (
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7 + i * 0.1 }}
              className="flex gap-4 items-start bg-cream rounded-2xl border border-oud-100/60 p-4 shadow-soft"
            >
              <div className="flex-none w-9 h-9 rounded-xl bg-gradient-to-br from-gold to-gold-deep text-sand flex items-center justify-center font-black text-sm shadow-soft">
                {i + 1}
              </div>
              <p className="text-sm text-oud-800 leading-relaxed pt-1.5">{step}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* External link */}
      {p.websiteUrl ? (
        <section className="mt-10">
          <a href={p.websiteUrl} target="_blank" rel="noreferrer noopener"
            className="group block relative overflow-hidden rounded-3xl bg-gradient-to-br from-oud-800 to-oud-900 p-6 shadow-quote">
            <div className="absolute inset-0 najdi-pattern opacity-15 pointer-events-none" aria-hidden />
            <div className="relative flex items-center justify-between gap-4">
              <div>
                <p className="text-[10px] tracking-[0.3em] text-gold-soft/70 font-bold uppercase">{p.brandName}</p>
                <p className="text-lg font-black text-sand mt-1">{p.websiteLabel}</p>
              </div>
              <div className="flex-none w-10 h-10 rounded-full bg-gold/20 border border-gold-soft/30 flex items-center justify-center group-hover:bg-gold/30 transition-colors">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-gold-soft" aria-hidden>
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><path d="M15 3h6v6" /><path d="M10 14L21 3" />
                </svg>
              </div>
            </div>
          </a>
        </section>
      ) : null}

      {/* WhatsApp */}
      {p.showWhatsapp && p.waNumber ? (
        <section className="mt-8 mb-4">
          <div className="flex items-center gap-3 mb-4">
            <Ornament className="flex-1" />
            <span className="text-sm font-bold text-oud-700 tracking-widest">تواصل معنا</span>
            <Ornament className="flex-1" />
          </div>
          <a href={`https://wa.me/${p.waNumber}`} target="_blank" rel="noreferrer noopener"
            className="flex items-center justify-center gap-2.5 bg-sage-deep text-sand rounded-2xl h-14 px-6 text-base font-bold shadow-soft hover:bg-sage-deep/90 transition-all active:scale-[0.98] w-full">
            {p.waButtonLabel}
          </a>
        </section>
      ) : null}
    </>
  );
}
